import * as Orm from '@core/services/orm/orm';
import { ORMMainContextInterface, PickAttachmentDetail, UpdatedFile } from "@archipad/controllers/ormMainContextInterface";
import { BehaviorSubject, firstValueFrom, Observable, of } from "rxjs";
import { map, switchMap } from "rxjs/operators";
import { AnnotatedEntity, AnnotationEntity, AnnotationService } from '@archipad-js/archipad/entity-services';
import { DrawingHint } from "@archipad/helpers/exif";
import { ProgressObserver } from '@archipad-js/core/progress';
import { PatchDescriptor, PatchesDescriptor } from '@archipad/services/file/patchFile';
import { prepareAttachment } from '@archipad/backend/archiweb/synchroDriver';
import { Data } from '@core/services/file/file';

import { AsyncUploadAttachmentService_DIID } from '@archipad/services/async-upload-attachment.service';
import _LegacyServiceManager from "@core/services/legacy-service-manager";
import { Account_Service_DIID } from '@archipad/services/account.service';
import { CacheFileDescriptor } from '@core/drivers/cacheFile/browser/cacheFileDescriptor';
/**
 *
 * Delegate that will be instantiated by the UI component that manages {@link AnnotatedEntity} to manage {@link AnnotationEntity}
 *
 * @see UIFormAttachmentInput from [Archipad](https://github.com/ArchipadDev/archipad)
 * @see UIIssueAttachment from [Archipad](https://github.com/ArchipadDev/archipad)
 */

export class AnnotatedEntityDelegate {
    /**
     * Filters notifications
     *
     * Keep only those that concerns the given AnnotatedEntity
     *
     * Keep only those based on their type from notifications pipe entityCreated, entityUpdated, entityDeleted and attachmentUpdated
    */
    public annotationEntities$: Observable<readonly AnnotationEntity[]>;

    private annotatedEntitySub: BehaviorSubject<AnnotatedEntity| null> = new BehaviorSubject(null);
    private readonly key: string = 'annotation.jpg';

    constructor(
        protected readonly ormMainContext: ORMMainContextInterface,
        private readonly annotationService: AnnotationService,
    ) {
        this.annotationEntities$ = this.annotatedEntitySub.pipe(
            switchMap((annotatedEntity) => {
                if (!annotatedEntity || annotatedEntity.isDeleted()) {
                    return of([]);
                }
                const annotationEntityType = this.annotationService.getAnnotationEntityTypeForAnnotated(annotatedEntity);
                const annotationEntityName = annotationEntityType.entityName as keyof EntityTypeMap;

                return Orm.fromEntityNotifications(
                    annotatedEntity,
                    [annotationEntityName],
                    [
                        Orm.NotificationType.entityCreated,
                        Orm.NotificationType.entityUpdated,
                        Orm.NotificationType.entityDeleted,
                        Orm.NotificationType.attachmentUpdated,
                    ],
                ).pipe(
                    map(() => {
                        if (annotatedEntity.isDeleted() || annotatedEntity.annotations.length === 0) {
                            return [];
                        }
                        return this.annotationService.getAnnotations(annotatedEntity)
                    }),
                );
            }),
        );
    }

    public setAnnotatedEntity(annotatedEntity: AnnotatedEntity | null): void {
        this.annotatedEntitySub.next(annotatedEntity);
    }

    /**
     * Ask user to pick one or more attachments.
    */
    public async pickAttachment(signal: AbortSignal, progress: ProgressObserver): Promise<UpdatedFile[]> {
        const annotatedEntity: AnnotatedEntity = this.annotatedEntitySub.getValue();
        const annotationEntity: AnnotationEntity = this.annotationService.makeAnnotationForAnnotated(annotatedEntity);
        annotationEntity.hasMetadata = true;
        annotationEntity.drawingHint = DrawingHint.DRAWING_NO;
        const pickAttachment: PickAttachmentDetail = {
            type: 'image',
            model: annotationEntity,
            key: this.key,
            height: 1000,
            width: 1000,
            aspectRatio: 1.0,
        };
        const updatedFile = await this.ormMainContext.pickAttachment(signal, progress, pickAttachment) ?? [];
        if (!updatedFile?.length) {
            annotationEntity.getContext().deleteEntity(annotationEntity);
        }
        return updatedFile;
    }

    public async pickAsyncNativeAttachment(signal: AbortSignal, progress: ProgressObserver): Promise<UpdatedFile[]> {
        const projectContext = await this.ormMainContext.getProjectContext(signal, progress);
        const project = projectContext.getProjectEntity();
        const currentAccount = await _LegacyServiceManager.container.get(Account_Service_DIID.symbol).fetchCurrentAccount();
        if (!currentAccount || !project) {
            return;
        }

        const asyncUploadAttachmentService = _LegacyServiceManager.container.get(AsyncUploadAttachmentService_DIID.symbol);
        const annotatedEntity: AnnotatedEntity = this.annotatedEntitySub.getValue();
        const annotationEntity: AnnotationEntity = this.annotationService.makeAnnotationForAnnotated(annotatedEntity);
        annotationEntity.hasMetadata = true;
        annotationEntity.drawingHint = DrawingHint.DRAWING_NO;
        const pickAttachment: PickAttachmentDetail = {
            type: 'image',
            model: annotationEntity,
            key: this.key,
            height: 1000,
            width: 1000,
            aspectRatio: 1.0,
        };
        const files = await this.ormMainContext.pickAsyncAttachment(signal, progress, pickAttachment) ?? [];
        if (!files?.length) {
            annotationEntity.getContext().deleteEntity(annotationEntity);
            return;
        }
        for (const file of files) {
            await asyncUploadAttachmentService.postUploadAttachmentTask(
                {
                    attachmentKey: this.key,
                    entityId: annotationEntity.id,
                    entityType: annotationEntity.entityType.entityName,
                    attachmentData: file,
                    userId: currentAccount.id.toString(),
                    projectId: project.id,
                },
            );
        }
        const tempDir = new PatchesDescriptor(project.id, "localCache");
        const tempPatch = new PatchDescriptor(tempDir, "current");
        const attachment = new CacheFileDescriptor(
            tempPatch,
            annotationEntity.id,
            this.key,
            project.id,
            currentAccount.id.toString(),
        );
        
        annotationEntity.setAttachmentForKey(this.key, attachment);
    }
    /**
     * Returns the attachment's signed URL of a given annotation.
    */
    public async getAttachmentUrl(annotationEntity: AnnotationEntity): Promise<string> {
        if (!annotationEntity) {
            return;
        }
        const attachmentUrl = await this.ormMainContext.getAttachment(annotationEntity, this.key);
        return attachmentUrl;
    }

    public async setAttachment(signal: AbortSignal, progress: ProgressObserver, file: File, projectId: string): Promise<AnnotationEntity> {
        const annotatedEntity: AnnotatedEntity = this.annotatedEntitySub.getValue();
        const annotationEntity: AnnotationEntity = this.annotationService.makeAnnotationForAnnotated(annotatedEntity);
        annotationEntity.hasMetadata = true;
        annotationEntity.drawingHint = DrawingHint.DRAWING_NO;
        const tempDir = new PatchesDescriptor(projectId, "temp");// Move this in ORMMainContextInterface
        const tempPatch = new PatchDescriptor(tempDir, "current"); // Move this in ORMMainContextInterface
        const bugNoteAttachment = await firstValueFrom(prepareAttachment(progress, tempPatch, Data.fromBlob(file), annotationEntity.id, this.key));// Move this in ORMMainContextInterface
        annotationEntity.setAttachmentForKey(this.key, bugNoteAttachment);
        return annotationEntity;
    }

    /**
     * Delete the given annotation entity.
    */
    public removeAnnotationEntity(annotationEntity: AnnotationEntity): void {
        annotationEntity.getContext().deleteEntity(annotationEntity);
    }

}
